import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const TeacherStudentsLeaveApplication = ({ onNavigate, userInfo }) => {
  // Page Specific State
  const [isMobile, setIsMobile] = useState(false);

  // Page Specific State
  const [activeTab, setActiveTab] = useState('leaves'); // 'leaves' or 'calendar'
  const [statusFilter, setStatusFilter] = useState('All'); // 'All', 'Pending', 'Approved', 'Rejected'
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Teacher Data
  const [teacherData, setTeacherData] = useState({
    name: "Loading...",
    teacherId: "...",
    department: "Computer Science",
    designation: "Lecturer",
    profileImage: null
  });

  // Mock Leaves Data
  const [leaves, setLeaves] = useState([
    { id: 1, studentName: 'Rahul Kumar', studentId: 'BCA001', department: 'Computer Science', semester: '3rd', type: 'Sick Leave', from: '2024-03-10', to: '2024-03-12', reason: 'Suffering from high fever', status: 'Pending', requestedOn: '2024-03-09', phone: '+91 9876543210', email: 'rahul@example.com', attachment: { type: 'image', url: 'https://via.placeholder.com/600x400/4CAF50/ffffff?text=Medical+Certificate' } },
    { id: 2, studentName: 'Priya Sharma', studentId: 'BCA005', department: 'Computer Science', semester: '3rd', type: 'Personal', from: '2024-03-15', to: '2024-03-16', reason: 'Family function', status: 'Approved', requestedOn: '2024-03-08', phone: '+91 9123456789', email: 'priya@example.com', attachment: null },
    { id: 3, studentName: 'Amit Singh', studentId: 'BCA012', department: 'Computer Science', semester: '3rd', type: 'Medical', from: '2024-03-20', to: '2024-03-25', reason: 'Surgery', status: 'Rejected', requestedOn: '2024-03-01', phone: '+91 9988776655', email: 'amit@example.com', attachment: { type: 'pdf', url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf', name: 'Surgery_Document.pdf' } },
  ]);

  // Mock Holiday Calendar Data
  const holidays = [
    { date: '2024-01-26', name: 'Republic Day', type: 'National Holiday' },
    { date: '2024-03-08', name: 'Maha Shivaratri', type: 'Religious Holiday' },
    { date: '2024-03-25', name: 'Holi', type: 'Religious Holiday' },
    { date: '2024-03-29', name: 'Good Friday', type: 'Religious Holiday' },
    { date: '2024-04-11', name: 'Eid-ul-Fitr', type: 'Religious Holiday' },
    { date: '2024-04-14', name: 'Dr. Ambedkar Jayanti', type: 'National Holiday' },
  ];

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch teacher profile data
  useEffect(() => {
    const fetchTeacherProfile = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        // Fallback if no token (for demo)
        if (!token) {
          setTeacherData({
            name: userInfo?.name || "Teacher User",
            teacherId: userInfo?.id || "TCH2024001",
            department: "Computer Science",
            designation: "Lecturer",
            profileImage: null
          });
          return;
        }

        const response = await fetch(`${API_LINK}/api/teacher/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setTeacherData(result.data);
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };
    fetchTeacherProfile();
  }, [userInfo]);



  // --- Logic for Leave Management ---
  const handleApprove = (id) => {
    setLeaves(leaves.map(leave => leave.id === id ? { ...leave, status: 'Approved' } : leave));
    alert('Leave Approved Successfully');
  };

  const handleReject = (id) => {
    setLeaves(leaves.map(leave => leave.id === id ? { ...leave, status: 'Rejected' } : leave));
    alert('Leave Rejected');
  };

  const handleViewDetails = (leave) => {
    setSelectedLeave(leave);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedLeave(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return '#28a745'; // Green
      case 'Rejected': return '#dc3545'; // Red
      default: return '#ffc107'; // Yellow/Orange
    }
  };

  return (
    <Layout role="teacher" onNavigate={onNavigate} activePage="teacher-student-leave-application" userInfo={userInfo}>
      {/* Header Card */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <h1 style={{ margin: '0 0 0.5rem', color: '#238548' }}>🏖️ Student Leave & Holiday Calendar</h1>
        <p style={{ color: '#666', margin: 0 }}>Manage student leave requests and view academic holidays</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
        <button
          onClick={() => setActiveTab('leaves')}
          style={{
            flex: 1,
            padding: '1rem',
            border: 'none',
            borderRadius: '8px',
            background: activeTab === 'leaves' ? '#238548' : '#e9ecef',
            color: activeTab === 'leaves' ? '#fff' : '#333',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.3s'
          }}
        >
          📋 Student Leave Applications
        </button>
        <button
          onClick={() => setActiveTab('calendar')}
          style={{
            flex: 1,
            padding: '1rem',
            border: 'none',
            borderRadius: '8px',
            background: activeTab === 'calendar' ? '#238548' : '#e9ecef',
            color: activeTab === 'calendar' ? '#fff' : '#333',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.3s'
          }}
        >
          📅 Holiday Calendar
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'leaves' ? (
        <>
          {/* Status Filter */}
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            {['All', 'Pending', 'Approved', 'Rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                style={{
                  padding: '0.6rem 1.25rem',
                  border: statusFilter === status ? '2px solid #238548' : '2px solid #e0e0e0',
                  borderRadius: '8px',
                  background: statusFilter === status ? '#238548' : '#ffffff',
                  color: statusFilter === status ? '#ffffff' : '#666',
                  fontSize: '0.9rem',
                  fontWeight: statusFilter === status ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: statusFilter === status ? '0 2px 8px rgba(35, 133, 72, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (statusFilter !== status) {
                    e.target.style.borderColor = '#97d6ac';
                    e.target.style.color = '#238548';
                  }
                }}
                onMouseLeave={(e) => {
                  if (statusFilter !== status) {
                    e.target.style.borderColor = '#e0e0e0';
                    e.target.style.color = '#666';
                  }
                }}
              >
                {status}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {leaves
              .filter(leave => statusFilter === 'All' || leave.status === statusFilter)
              .map(leave => (
                <div key={leave.id} style={{
                  background: '#ffffff',
                  padding: '1.25rem',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  borderLeft: `5px solid ${getStatusColor(leave.status)}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <h3 style={{ margin: '0 0 0.75rem', color: '#333', fontSize: '1.1rem' }}>{leave.studentName} <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: 'normal' }}>({leave.studentId})</span></h3>
                      <p style={{ margin: '0 0 0.5rem', color: '#555', fontSize: '0.95rem' }}><strong>Date:</strong> {formatDate(leave.from)} to {formatDate(leave.to)}</p>
                      <p style={{ margin: '0 0 0.5rem', color: '#555', fontSize: '0.95rem' }}><strong>Reason:</strong> {leave.reason}</p>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#999' }}>Requested on: {formatDate(leave.requestedOn)}</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.75rem' }}>
                      <span style={{
                        padding: '0.4rem 1rem',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        background: `${getStatusColor(leave.status)}20`,
                        color: getStatusColor(leave.status)
                      }}>
                        {leave.status}
                      </span>
                      <button
                        onClick={() => handleViewDetails(leave)}
                        style={{ background: '#238548', color: '#fff', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.9rem' }}
                      >
                        View Details
                      </button>
                      {leave.status === 'Pending' && (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleApprove(leave.id)}
                            style={{ background: '#28a745', color: '#fff', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.9rem' }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(leave.id)}
                            style={{ background: '#dc3545', color: '#fff', border: 'none', padding: '0.5rem 1.25rem', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.9rem' }}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            {leaves.filter(leave => statusFilter === 'All' || leave.status === statusFilter).length === 0 && (
              <div style={{
                padding: '3rem',
                textAlign: 'center',
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                <p style={{ color: '#666', fontSize: '1.1rem', margin: 0 }}>
                  No {statusFilter !== 'All' ? statusFilter.toLowerCase() : ''} leave applications found.
                </p>
              </div>
            )}
          </div>
        </>
      ) : (
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ borderBottom: '2px solid #f0f0f0', paddingBottom: '1rem', marginBottom: '1.5rem', color: '#333' }}>Academic Holidays 2024</h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {holidays.map((holiday, index) => (
              <div key={index} style={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '1.5rem',
                textAlign: 'center',
                transition: 'transform 0.3s',
                background: 'linear-gradient(to bottom right, #ffffff, #f9f9f9)'
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-5px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📅</div>
                <h3 style={{ margin: '0 0 0.5rem', color: '#238548' }}>{holiday.name}</h3>
                <p style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#333', margin: '0 0 0.5rem' }}>{formatDate(holiday.date)}</p>
                <span style={{ fontSize: '0.85rem', background: '#e0f2f1', color: '#00695c', padding: '0.25rem 0.75rem', borderRadius: '15px' }}>{holiday.type}</span>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* View Details Modal */}
      {showModal && selectedLeave && (
        <div onClick={handleCloseModal} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff',
            borderRadius: '12px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: '#238548',
              color: '#fff',
              padding: '1.5rem',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.3rem' }}>Leave Application Details</h2>
              <button onClick={handleCloseModal} style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#fff',
                width: '35px',
                height: '35px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '1.2rem',
                fontWeight: 'bold'
              }}>✕</button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '2rem' }}>
              {/* Student Info */}
              <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '2px solid #f0f0f0' }}>
                <h3 style={{ margin: '0 0 1rem', color: '#333', fontSize: '1.1rem' }}>Student Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem' }}>
                  <div><strong>Name:</strong> {selectedLeave.studentName}</div>
                  <div><strong>Student ID:</strong> {selectedLeave.studentId}</div>
                  <div><strong>Department:</strong> {selectedLeave.department}</div>
                  <div><strong>Semester:</strong> {selectedLeave.semester}</div>
                  <div><strong>Phone:</strong> {selectedLeave.phone}</div>
                  <div><strong>Email:</strong> {selectedLeave.email}</div>
                </div>
              </div>

              {/* Leave Details */}
              <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '2px solid #f0f0f0' }}>
                <h3 style={{ margin: '0 0 1rem', color: '#333', fontSize: '1.1rem' }}>Leave Details</h3>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <div><strong>Leave Type:</strong> {selectedLeave.type}</div>
                  <div><strong>From Date:</strong> {formatDate(selectedLeave.from)}</div>
                  <div><strong>To Date:</strong> {formatDate(selectedLeave.to)}</div>
                  <div><strong>Requested On:</strong> {formatDate(selectedLeave.requestedOn)}</div>
                  <div><strong>Status:</strong> <span style={{
                    padding: '0.3rem 0.75rem',
                    borderRadius: '15px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    background: `${getStatusColor(selectedLeave.status)}20`,
                    color: getStatusColor(selectedLeave.status)
                  }}>{selectedLeave.status}</span></div>
                  <div style={{ marginTop: '0.5rem' }}><strong>Reason:</strong><br /><p style={{ margin: '0.5rem 0 0', padding: '0.75rem', background: '#f8f9fa', borderRadius: '6px', color: '#555' }}>{selectedLeave.reason}</p></div>
                </div>
              </div>

              {/* Attachments */}
              {selectedLeave.attachment && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: '0 0 1rem', color: '#333', fontSize: '1.1rem' }}>Attached Document</h3>
                  {selectedLeave.attachment.type === 'image' ? (
                    <div style={{ textAlign: 'center', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                      <img src={selectedLeave.attachment.url} alt="Leave attachment" style={{
                        maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }} />
                    </div>
                  ) : (
                    <div style={{ padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📄</div>
                      <p style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '500' }}>{selectedLeave.attachment.name || 'Document.pdf'}</p>
                      <a href={selectedLeave.attachment.url} target="_blank" rel="noopener noreferrer" style={{
                        display: 'inline-block',
                        background: '#238548',
                        color: '#fff',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontWeight: '500'
                      }}>Open PDF</a>
                    </div>
                  )}
                </div>
              )}

              {!selectedLeave.attachment && (
                <div style={{ padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center', color: '#666' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📋</div>
                  <p style={{ margin: 0 }}>No attachments provided</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1.5rem',
              background: '#f8f9fa',
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: '12px',
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button onClick={handleCloseModal} style={{
                background: '#6c757d', color: '#fff', border: 'none', padding: '0.75rem 1.5rem',
                borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.95rem'
              }}>Close</button>
              {selectedLeave.status === 'Pending' && (
                <>
                  <button onClick={() => { handleReject(selectedLeave.id); handleCloseModal(); }} style={{
                    background: '#dc3545', color: '#fff', border: 'none', padding: '0.75rem 1.5rem',
                    borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.95rem'
                  }}>Reject</button>
                  <button onClick={() => { handleApprove(selectedLeave.id); handleCloseModal(); }} style={{
                    background: '#28a745', color: '#fff', border: 'none', padding: '0.75rem 1.5rem',
                    borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.95rem'
                  }}>Approve</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default TeacherStudentsLeaveApplication;  